/**
 * Lead matches for a ringing inbound call (split out of use-inbound-voice.ts).
 *
 * The ring's custom parameters carry only the backend's best match. Several
 * leads can share a caller's number, so while the call rings the provider asks
 * `GET /lead/by-phone` for all of them — same matching and order as the inbound
 * route — and the agent can pick another before answering. A failed lookup
 * leaves the ring as the parameters described it.
 */
import * as Sentry from "@sentry/react";
import type { TLeadPhoneMatch } from "@/models/leads.model";
import { getLeadsByPhone, reassignInboundCallLead } from "@/services/inbound-caller.service";
import type { IncomingCallInfo } from "./use-inbound-voice";

/** How many matches the banner lists; `leadMatchTotal` still counts them all. */
const MATCH_LIMIT = 10;

export const leadMatchName = (lead: Pick<TLeadPhoneMatch, "firstName" | "lastName">): string | null =>
  `${lead.firstName ?? ""} ${lead.lastName ?? ""}`.trim() || null;

/**
 * The ring's lead fields once the matches are known, or null when there is
 * nothing to add (withheld caller, lookup failed). A ring that came without a
 * lead adopts the best match, so the popup still opens on accept — the accept
 * then moves the call row onto it (see `syncInboundCallLead`).
 */
export async function loadLeadMatches(
  info: IncomingCallInfo,
): Promise<Pick<IncomingCallInfo, "leadMatches" | "leadMatchTotal" | "leadId" | "leadName"> | null> {
  if (!info.fromE164) return null;
  try {
    const { total, leads } = await getLeadsByPhone(info.fromE164, MATCH_LIMIT);
    const best = leads[0];
    const adoptBest = info.leadId === null && !!best;
    return {
      leadMatches: leads,
      leadMatchTotal: total,
      leadId: adoptBest ? best.id : info.leadId,
      leadName: adoptBest ? leadMatchName(best) : info.leadName,
    };
  } catch (err) {
    Sentry.captureException(err, {
      tags: { component: "InboundVoice", operation: "loadLeadMatches" },
      extra: { sessionName: info.sessionName },
    });
    return null;
  }
}

/**
 * The agent answered as a different lead than the one the backend attached to
 * the call row: move the row, so the call's interaction and activity land on
 * the lead the agent chose. Fire-and-forget — answering never waits on it.
 */
export function syncInboundCallLead(info: IncomingCallInfo): void {
  if (!info.sessionName || info.leadId === null || info.leadId === info.routedLeadId) return;
  reassignInboundCallLead(info.sessionName, info.leadId).catch((err) => {
    Sentry.captureException(err, {
      tags: { component: "InboundVoice", operation: "reassignLead" },
      extra: { sessionName: info.sessionName, leadId: info.leadId, routedLeadId: info.routedLeadId },
    });
  });
}
